/**
 * Naming a party consistently across all three steps.
 *
 * Port of Scale's UpdateInvoice/HourlyUpdateInvoice/partyLabel.js.
 *
 * On a real case three of four parties can share a first name — case 2839 has
 * "ibrahim" as Plaintiff 2, Defendant and Defendant 2 — so the name alone does
 * not identify anyone. The role and position prefix is the only discriminator,
 * and it has to come from the party's OWN case_parties record rather than from
 * its index in an array, or the tab and the invoice summary disagree about who
 * is on screen.
 *
 * `getPartyTypeByRole` is INLINED rather than imported. Scale imports it from
 * `utils/utils`; CM has an equivalent in
 * components/case-detail/parties/partyUtils.js, but pulling a case-detail util
 * into the invoice wizard couples two unrelated surfaces — and this module then
 * has zero imports of its own, exactly like similarity.js beside it.
 *
 * NOTE ON `position`: the edit-context endpoint returns the RAW INTEGER from
 * case_parties (2, 3, …), never a pre-rendered "Plaintiff 2". getPartyTypeByRole
 * is what turns it into a label, and it must be given the raw number.
 */

/** Verbatim behaviour of Scale's utils/utils.js getPartyTypeByRole. */
export const getPartyTypeByRole = (
    role,
    position = null,
    is_odr_mediator = false,
    isSystemCoupon = false,
    is_car_case_not_scheduled = false
) => {
    if (role === 'plaintiff' || role === 'primary_plaintiff') {
        return is_car_case_not_scheduled
            ? 'Initiating Party'
            : isSystemCoupon
              ? 'Initiating Party'
              : is_odr_mediator
                ? position && position > 1
                    ? `Party ${position + 1}`
                    : 'Party 1'
                : `Plaintiff${position ? ` ${position}` : ''}`
    } else if (role === 'defendant' || role === 'primary_defendant') {
        return is_car_case_not_scheduled
            ? 'Responding Party'
            : isSystemCoupon
              ? 'Responding Party'
              : is_odr_mediator
                ? position && position > 1
                    ? `Party ${position + 1}`
                    : 'Party 2'
                : `Defendant${position ? ` ${position}` : ''}`
    }
    return ''
}

/**
 * Resolve a payment row to { side, role, position, name }.
 *
 * Dereferences the NESTED case payload: caseInfo.plaintiff.client_id,
 * caseInfo.defendant.client_id and their .additionalParties[]. A flat
 * `parties[]` array cannot answer "which side" and every additional party would
 * render as a bare name.
 */
export const partyMetaOf = (row, caseInfo) => {
    const cid = row?.client_id
    const nameOf = (p) => p?.name || row?.clients?.name || ''

    if (cid != null && caseInfo?.plaintiff?.client_id === cid) {
        return {
            side: 'plaintiff',
            role: 'primary_plaintiff',
            position: 0,
            name: nameOf(caseInfo.plaintiff),
        }
    }
    if (cid != null && caseInfo?.defendant?.client_id === cid) {
        return {
            side: 'defendant',
            role: 'primary_defendant',
            position: 0,
            name: nameOf(caseInfo.defendant),
        }
    }

    const inPlaintiff = (caseInfo?.plaintiff?.additionalParties || []).find(
        (p) => p?.client_id === cid
    )
    if (inPlaintiff) {
        return {
            side: 'plaintiff',
            role: inPlaintiff.role || 'plaintiff',
            position: inPlaintiff.position ?? 1,
            name: nameOf(inPlaintiff),
        }
    }

    const inDefendant = (caseInfo?.defendant?.additionalParties || []).find(
        (p) => p?.client_id === cid
    )
    if (inDefendant) {
        return {
            side: 'defendant',
            role: inDefendant.role || 'defendant',
            position: inDefendant.position ?? 1,
            name: nameOf(inDefendant),
        }
    }

    return { side: null, role: null, position: 0, name: row?.clients?.name || '' }
}

/**
 * The full party record behind a payment row — primary or additional.
 * Used to seed the bill-to card and the preview's Bill To block from the case
 * rather than from the (thinner) `clients` embed on the invoice row.
 */
export const partyRecordOf = (row, caseInfo) => {
    const cid = row?.client_id
    if (cid == null) return null
    if (caseInfo?.plaintiff?.client_id === cid) return caseInfo.plaintiff
    if (caseInfo?.defendant?.client_id === cid) return caseInfo.defendant
    return (
        (caseInfo?.plaintiff?.additionalParties || []).find((p) => p?.client_id === cid) ||
        (caseInfo?.defendant?.additionalParties || []).find((p) => p?.client_id === cid) ||
        null
    )
}

/** Just the role: "Plaintiff 2". Used where the name is rendered separately. */
export const partyRoleFor = (row, caseInfo, mediatorRecord) => {
    const meta = partyMetaOf(row, caseInfo)
    if (!meta.role) return 'Party'
    try {
        return getPartyTypeByRole(
            meta.role,
            meta.position,
            mediatorRecord?.is_odr_mediator
        )
    } catch (_) {
        return meta.side === 'plaintiff' ? 'Plaintiff' : 'Defendant'
    }
}

/** "Plaintiff 2 - ibrahim". Falls back to the name when the side is unknown. */
export const partyLabelFor = (row, caseInfo, mediatorRecord) => {
    const meta = partyMetaOf(row, caseInfo)
    const name = meta.name || row?.clients?.name || ''
    if (!meta.role) return name || 'Party'
    const role = partyRoleFor(row, caseInfo, mediatorRecord)
    return name ? `${role} - ${name}` : role
}
